import { Camera, ChevronRight, HardDrive, Monitor, Server } from 'lucide-react';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import type { Device } from '../types';
import { deviceTypeLabels } from '../utils/format';
import { MetricBar } from './MetricBar';
import { StatusBadge } from './StatusBadge';
import { CameraOverallBadge } from './CameraOverallBadge';
import { DeviceListItem } from './DeviceListItem';

const typeIcons = {
  camera: Camera,
  server: Server,
  computer: Monitor,
  recorder: HardDrive,
};

const metricTone = (value: number) => {
  if (value < 45) return 'critical' as const;
  if (value < 75) return 'warning' as const;
  return 'success' as const;
};

export const DeviceCard = memo(function DeviceCard({ device, compact = false, variant = 'card' }: { device: Device; compact?: boolean; variant?: 'card' | 'list' }) {
  if (variant === 'list') return <DeviceListItem device={device} />;

  const Icon = typeIcons[device.type];
  const keyMetric =
    device.type === 'camera'
      ? device.telemetry.latencyMs != null
        ? `${Math.round(device.telemetry.latencyMs)} мс`
        : 'Нет данных'
      : device.telemetry.cpu != null
        ? `CPU ${Math.round(device.telemetry.cpu)}%`
        : 'Нет данных';

  return (
    <Link
      to={device.type === 'camera' ? `/cameras/${device.id}` : `/devices/${device.id}`}
      className={`device-card ${compact ? 'device-card--compact' : ''}`}
      aria-label={`${device.name}, ${deviceTypeLabels[device.type]}, ${device.status}`}
    >
      <div className="device-card__topline">
        <span className={`device-icon device-icon--${device.type}`} aria-hidden="true">
          <Icon size={20} />
        </span>
        <div className="device-card__identity">
          <strong>{device.name}</strong>
          <span>{deviceTypeLabels[device.type]} · {device.location}</span>
        </div>
        <ChevronRight className="device-card__chevron" size={18} aria-hidden="true" />
      </div>
      <div className="device-card__status-row">
        {device.type === 'camera'
          ? <CameraOverallBadge device={device} compact />
          : <StatusBadge status={device.status} compact />}
        <span>{device.type === 'camera' ? device.camera?.recorderName || 'DVR не назначен' : <span className="mono">{device.ip}</span>}</span>
      </div>
      {!compact && device.type !== 'camera' && <MetricBar label="Health" value={device.health} tone={metricTone(device.health)} />}
      <div className="device-card__meta">
        <span>{keyMetric}</span>
        <span>Связь: {device.lastResponse}</span>
      </div>
    </Link>
  );
});
